import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  useCallback,
} from "react";
import { X, Search, Sparkles } from "lucide-react";

// ==========================================
// 1. STYLES & ANIMATIONS (No Tailwind)
// ==========================================

// We inject these keyframes for the "Ping" (radar) and "Blob" (background) animations
const GlobalStyles = () => (
  <style>{`
    @keyframes ping-custom {
      75%, 100% { transform: scale(2); opacity: 0; }
    }
    @keyframes blob-bounce {
      0% { transform: translate(0, 0) scale(1); }
      33% { transform: translate(30px, -50px) scale(1.1); }
      66% { transform: translate(-20px, 20px) scale(0.9); }
      100% { transform: translate(0, 0) scale(1); }
    }
  `}</style>
);

const styles = {
  // Brand Gradients
  brandGradientBg: "linear-gradient(to top right, #fc8c66, #F76a7b)",
  brandGradientText: {
    backgroundImage: "linear-gradient(to right, #fc8c66, #F76a7b)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    color: "transparent",
  },

  // Card Container
  card: {
    position: "relative",
    width: "100%",
    maxWidth: "420px",
    margin: "0 16px",
    padding: "20px",
    borderRadius: "24px", // Rounded-3xl
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    display: "flex",
    alignItems: "flex-start",
    gap: "16px",
    borderWidth: "1px",
    borderStyle: "solid",
    transition: "all 0.5s cubic-bezier(0.19, 1, 0.22, 1)",
    pointerEvents: "auto",
    marginBottom: "12px",
    userSelect: "none",
    boxSizing: "border-box",
  },

  // Typography
  title: {
    fontSize: "17px",
    fontWeight: "700",
    lineHeight: "1.2",
    marginBottom: "4px",
    letterSpacing: "-0.01em",
    margin: 0,
  },
  message: {
    fontSize: "13px",
    color: "#64748b", // slate-500
    lineHeight: "1.5",
    fontWeight: "500",
    margin: 0,
  },

  // Icons
  iconWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "48px",
    height: "48px",
  },
  pingCircle: {
    position: "absolute",
    height: "100%",
    width: "100%",
    borderRadius: "50%",
    opacity: 0.2,
    animation: "ping-custom 1s cubic-bezier(0, 0, 0.2, 1) infinite",
    background: "linear-gradient(to top right, #fc8c66, #F76a7b)",
  },
  innerGlow: {
    position: "absolute",
    height: "100%",
    width: "100%",
    borderRadius: "50%",
    opacity: 0.1,
    background: "linear-gradient(to top right, #fc8c66, #F76a7b)",
  },
  mainIcon: {
    position: "relative",
    borderRadius: "50%",
    height: "48px",
    width: "48px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 10px 15px -3px rgba(249, 115, 22, 0.3)", // orange shadow
    background: "linear-gradient(to top right, #fc8c66, #F76a7b)",
  },

  // Close Button
  closeBtn: {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    padding: "6px",
    marginRight: "-8px",
    marginTop: "-8px",
    color: "#cbd5e1", // slate-300
    transition: "color 0.2s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  // Progress Bar
  progressBarContainer: {
    position: "absolute",
    bottom: 0,
    left: "20px",
    right: "20px",
    height: "2px",
    borderRadius: "999px",
    overflow: "hidden",
  },
  progressBarFill: {
    height: "100%",
    background: "linear-gradient(to top right, #fc8c66, #F76a7b)",
    transition: "width 0.1s linear",
    opacity: 0.6,
  },
};

// ==========================================
// 2. CONTEXT & HOOK
// ==========================================
const NotificationContext = createContext(null);

export const useNotification = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error(
      "useNotification must be used within a NotificationProvider"
    );
  }
  return ctx;
};

// ==========================================
// 3. NOTIFICATION CARD COMPONENT
// ==========================================
const NotificationCard = ({ id, title, message, type, duration, onRemove }) => {
  const [exiting, setExiting] = useState(false);
  const [entered, setEntered] = useState(false);
  const [progress, setProgress] = useState(100);

  // Configuration for different card types
  const config = {
    expansion: {
      renderIcon: () => (
        <div style={styles.iconWrapper}>
          <span style={styles.pingCircle} />
          <span style={styles.innerGlow} />
          <div style={styles.mainIcon}>
            <Search color="white" size={24} strokeWidth={2.5} />
          </div>
        </div>
      ),
      containerStyle: {
        borderColor: "rgba(255, 237, 213, 0.5)", // orange-100/50
        boxShadow: "0 20px 40px -10px rgba(252, 140, 102, 0.3)",
      },
      titleStyle: styles.brandGradientText,
    },
    default: {
      renderIcon: () => (
        <div
          style={{
            width: "40px",
            height: "40px",
            borderRadius: "50%",
            backgroundColor: "#f8fafc", // slate-50
            border: "1px solid #f1f5f9", // slate-100
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#94a3b8", // slate-400
          }}
        >
          <Sparkles size={20} strokeWidth={1.5} />
        </div>
      ),
      containerStyle: {
        borderColor: "#f1f5f9", // slate-100
        boxShadow: "0 8px 30px rgba(0,0,0,0.06)",
      },
      titleStyle: { color: "#1e293b" }, // slate-800
    },
  };

  const cfg = config[type] || config.default;

  // Entrance Animation Trigger
  useEffect(() => {
    requestAnimationFrame(() => setEntered(true));
  }, []);

  // Timer Logic
  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((p) => {
        if (p <= 0) {
          clearInterval(timer);
          dismiss();
          return 0;
        }
        return p - 100 / (duration / 100);
      });
    }, 100);
    return () => clearInterval(timer);
  }, [duration]);

  const dismiss = () => {
    setExiting(true);
    setTimeout(() => onRemove(id), 500); // Wait for exit animation
  };

  // Dynamic Styles for Animation
  const animationStyle = {
    transform:
      !entered || exiting
        ? "translateY(12px) scale(0.95)"
        : "translateY(0) scale(1)",
    opacity: !entered || exiting ? 0 : 1,
  };

  return (
    <div style={{ ...styles.card, ...cfg.containerStyle, ...animationStyle }}>
      {/* Icon Section */}
      <div style={{ flexShrink: 0, paddingTop: "4px" }}>{cfg.renderIcon()}</div>

      {/* Content Section */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <h4 style={{ ...styles.title, ...cfg.titleStyle }}>{title}</h4>
        <p style={styles.message}>{message}</p>
      </div>

      {/* Close Button */}
      <button
        onClick={dismiss}
        style={styles.closeBtn}
        onMouseEnter={(e) => (e.currentTarget.style.color = "#64748b")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "#cbd5e1")}
      >
        <X size={18} />
      </button>

      {/* Progress Bar */}
      <div style={styles.progressBarContainer}>
        <div style={{ ...styles.progressBarFill, width: `${progress}%` }} />
      </div>
    </div>
  );
};

// ==========================================
// 4. PROVIDER COMPONENT
// ==========================================
export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const showNotification = useCallback((title, message, type = "default") => {
    const id = Date.now().toString();
    setNotifications((prev) => [
      ...prev,
      { id, title, message, type, duration: 10000 },
    ]);
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      <GlobalStyles />
      {children}

      {/* Toast Container - Fixed at Bottom Center */}
      <div
        style={{
          position: "fixed",
          bottom: "40px",
          left: 0,
          right: 0,
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pointerEvents: "none",
        }}
      >
        {notifications.map((n) => (
          <NotificationCard key={n.id} {...n} onRemove={removeNotification} />
        ))}
      </div>
    </NotificationContext.Provider>
  );
};
